import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "./../../../assets/css/admin/gestionCategorie/AdminCategoriesPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";

import useCategorie from "../../../hooks/useCategorie.js";
import useGroupeCategorie from "../../../hooks/useGroupeCategorie.js";
import IconPicker from "../../../components/icon/IconPicker.jsx";

export default function CategorieEditPage({ setAlert }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { getOneCategorie, updateCategorie } = useCategorie();
  const { fetchGroupes } = useGroupeCategorie();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Groupes : state local
  const [loadingGroupes, setLoadingGroupes] = useState(false);
  const [groupes, setGroupes] = useState([]);

  const [form, setForm] = useState({
    nom: "",
    archive: 0,
    idCategorieGroupe: "",
    rang: 0,
    fields: [],
    premium: 2, // 1 = Premium, 2 = Standard
    hasPrice: 1, // 1 = Oui, 2 = Non
    prixAbonnement: 0,
    prixBoost: 0,
  });

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Catégories", link: "/admin/categories" },
    { label: "Modification" },
  ];

  const loadGroupes = async () => {
    setLoadingGroupes(true);
    try {
      const res = await fetchGroupes();
      const list = res?.data?.data ?? res?.data ?? res ?? [];
      setGroupes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      setGroupes([]);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la liste des groupes.",
      });
    } finally {
      setLoadingGroupes(false);
    }
  };

  useEffect(() => {
    loadGroupes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...(prev.fields || []), { name: "", icon: "" }],
    }));
  };

  const updateField = (index, key, value) => {
    setForm((prev) => {
      const newFields = [...(prev.fields || [])];
      newFields[index] = { ...newFields[index], [key]: value };
      return { ...prev, fields: newFields };
    });
  };

  const removeField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    const loadCategorie = async () => {
      if (!id) return;

      try {
        setLoadingData(true);
        const res = await getOneCategorie(id);

        const data = res?.data?.data ?? res?.data ?? null;
        
        if (!data) {
          setAlert?.({ type: "danger", message: "Catégorie introuvable." });
          navigate("/admin/categories");
          return;
        }

        setForm({
          nom: data?.nom ?? "",
          archive: Number(data?.archive ?? 0),
          idCategorieGroupe:
            data?.idCategorieGroupe?._id ?? data?.idCategorieGroupe ?? "",
          rang: Number(data?.rang ?? 0),
          fields: Array.isArray(data?.fields) ? data.fields : [],
          premium: Number(data?.premium ?? 2),
          hasPrice: Number(data?.hasPrice ?? 1),
          prixAbonnement: Number(data?.prixAbonnement ?? 0),
          prixBoost: Number(data?.prixBoost ?? 0),
        });
      } catch (err) {
        console.error(err);
        setAlert?.({
          type: "danger",
          message: "Erreur lors du chargement de la catégorie.",
        });
      } finally {
        setLoadingData(false);
      }
    };

    loadCategorie();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const cleanedFields = (form.fields || []).map((f) => ({
        name: String(f?.name ?? "").trim(),
        icon: String(f?.icon ?? "").trim(),
      }));

      const payload = {
        ...form,
        rang: Number(form.rang ?? 0),
        archive: Number(form.archive ?? 0),
        fields: cleanedFields,
        premium: Number(form.premium ?? 2),
        hasPrice: Number(form.hasPrice ?? 1),
      };

      await updateCategorie(id, payload);

      setAlert?.({ type: "success", message: "Catégorie mise à jour." });
      navigate(location.state?.from || "/admin/categories");
    } catch (err) {
      console.error(err);
      setAlert?.({
        type: "danger",
        message: "Impossible de mettre à jour la catégorie.",
      });
    } finally {
      setLoading(false);
    }
  };

  const changePreminumByIdGroupCategory = (value) => {
    groupes.forEach((groupe) => {
      if (groupe._id == value) {
        setForm((prev) => ({
          ...prev,
          premium: groupe.type,
        }));
      }
    });
  };

  return (
    <div className="adminCategoriesPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminCategoriesGrid">
          <section className="adminCategoriesCard adminCategoriesCardCenter">
            {/* Header */}
            <div className="adminCategoriesHeader">
              <div className="adminCategoriesHeaderLeft">
                <div className="adminCategoriesTitle">
                  Modifier la catégorie
                </div>
                <div className="adminCategoriesSubtitle">
                  {loadingData ? "Chargement..." : "Modifiez puis enregistrez."}
                </div>
              </div>

              <div className="adminCategoriesHeaderRight">
                <Link
                  to={location.state?.from || "/categories"}
                  className="adminCategoriesBtn adminCategoriesBtnSoft"
                  title="Retour"
                >
                  <i className="bi bi-arrow-left" />
                  <span>Retour</span>
                </Link>

                <button
                  type="submit"
                  form="adminCategoriesForm"
                  className="adminCategoriesBtn adminCategoriesBtnPrimary"
                  disabled={loading || loadingData}
                  title="Enregistrer"
                >
                  <i className="bi bi-check2-circle" />
                  <span>{loading ? "Mise à jour..." : "Enregistrer"}</span>
                </button>
              </div>
            </div>

            {/* Body */}
            {loadingData ? (
              <div className="adminCategoriesEmpty">
                <div className="adminCategoriesSpinner" />
                <div>Chargement de la catégorie...</div>
              </div>
            ) : (
              <form
                id="adminCategoriesForm"
                className="adminCategoriesForm"
                onSubmit={handleSubmit}
              >
                <div className="adminCategoriesFormGrid">
                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">
                      Nom de la catégorie
                    </label>
                    <input
                      className="adminCategoriesInput"
                      value={form.nom}
                      onChange={(e) =>
                        setForm({ ...form, nom: e.target.value })
                      }
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">Groupe</label>

                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        className="adminCategoriesSelect"
                        value={form.idCategorieGroupe}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            idCategorieGroupe: e.target.value,
                          });
                          changePreminumByIdGroupCategory(e.target.value);
                        }}
                        required
                        disabled={loading || loadingGroupes}
                        style={{ flex: 1 }}
                      >
                        <option value="">
                          {loadingGroupes
                            ? "Chargement des groupes..."
                            : "-- Groupe de catégorie --"}
                        </option>

                        {groupes.map((g) => (
                          <option key={g._id} value={g._id}>
                            {g.nom}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="adminCategoriesBtn adminCategoriesBtnSoft"
                        onClick={loadGroupes}
                        disabled={loading || loadingGroupes}
                        title="Rafraîchir les groupes"
                        style={{ padding: "8px 10px" }}
                      >
                        <i className="bi bi-arrow-clockwise" />
                      </button>
                    </div>
                  </div>

                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">Rang</label>
                    <input
                      type="number"
                      className="adminCategoriesInput"
                      value={form.rang}
                      onChange={(e) =>
                        setForm({ ...form, rang: Number(e.target.value) })
                      }
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">Statut</label>
                    <select
                      className="adminCategoriesSelect"
                      value={String(form.archive)}
                      onChange={(e) =>
                        setForm({ ...form, archive: Number(e.target.value) })
                      }
                      disabled={loading}
                    >
                      <option value="0">Actif</option>
                      <option value="1">Archivé</option>
                    </select>
                  </div>

                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">Type</label>
                    <select
                      className="adminCategoriesSelect"
                      value={String(form.premium)}
                      onChange={(e) =>
                        setForm({ ...form, premium: Number(e.target.value) })
                      }
                      disabled={loading}
                    >
                      <option value="1">Premium</option>
                      <option value="2">Standard</option>
                    </select>
                  </div>

                  <div className="adminCategoriesFormGroup">
                    <label className="adminCategoriesLabel">Prix</label>
                    <select
                      className="adminCategoriesSelect"
                      value={String(form.hasPrice)}
                      onChange={(e) =>
                        setForm({ ...form, hasPrice: Number(e.target.value) })
                      }
                      disabled={loading}
                    >
                      <option value="1">Oui</option>
                      <option value="2">Non</option>
                    </select>
                  </div>

                  {form.hasPrice === 1 && (
                    <>
                      <div className="adminCategoriesFormGroup">
                        <label className="adminCategoriesLabel">
                          Prix abonnement
                        </label>
                        <input
                          type="number"
                          className="adminCategoriesInput"
                          value={form.prixAbonnement}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              prixAbonnement: Number(e.target.value),
                            })
                          }
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="adminCategoriesFormGroup">
                        <label className="adminCategoriesLabel">
                          Prix boost
                        </label>
                        <input
                          type="number"
                          className="adminCategoriesInput"
                          value={form.prixBoost}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              prixBoost: Number(e.target.value),
                            })
                          }
                          required
                          disabled={loading}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="adminCategoriesDivider" />

                <div className="adminCategoriesSectionHead">
                  <div className="adminCategoriesSectionTitle">
                    Champs personnalisés
                  </div>
                  <button
                    type="button"
                    className="adminCategoriesBtn adminCategoriesBtnSoft"
                    onClick={addField}
                    disabled={loading}
                  >
                    <i className="bi bi-plus-lg" />
                    <span>Ajouter</span>
                  </button>
                </div>

                {(form.fields || []).length === 0 ? (
                  <div className="adminCategoriesHint">
                    Aucun champ personnalisé pour le moment.
                  </div>
                ) : (
                  <div className="adminCategoriesFieldsWrap">
                    {(form.fields || []).map((f, i) => (
                      <div className="adminCategoriesFieldRow" key={i}>
                        <div className="adminCategoriesFieldLeft">
                          <label className="adminCategoriesLabel">
                            Nom du champ
                          </label>
                          <input
                            className="adminCategoriesInput"
                            value={f.name}
                            onChange={(e) =>
                              updateField(i, "name", e.target.value)
                            }
                            required
                            disabled={loading}
                          />

                          <label className="adminCategoriesLabel mt8">
                            Icône
                          </label>
                          <div className="adminCategoriesIconPickerWrap">
                            <IconPicker
                              value={f.icon}
                              onSelect={(iconClass) =>
                                updateField(i, "icon", iconClass)
                              }
                            />
                          </div>
                        </div>

                        <div className="adminCategoriesFieldRight">
                          <button
                            type="button"
                            className="adminCategoriesIconBtn danger"
                            title="Supprimer ce champ"
                            onClick={() => removeField(i)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="adminCategoriesFormActions">
                  <Link
                    to={location.state?.from || "/categories"}
                    className="adminCategoriesBtn adminCategoriesBtnSoft"
                  >
                    <i className="bi bi-x-circle" />
                    <span>Annuler</span>
                  </Link>

                  <button
                    type="submit"
                    className="adminCategoriesBtn adminCategoriesBtnPrimary"
                    disabled={loading}
                  >
                    <i className="bi bi-check2-circle" />
                    <span>{loading ? "Mise à jour..." : "Enregistrer"}</span>
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
